import React, { useEffect, useState } from 'react';
import { FileEdit, Clock, CheckCircle2, XCircle, X, MessageSquare } from 'lucide-react';
import {
  getAllChangeRequests,
  approveChangeRequest,
  rejectChangeRequest,
  type ChangeRequest,
} from '../../../services/changeRequestService.ts';

type TabType = 'pending' | 'approved' | 'rejected';

const statusBadge = (status: ChangeRequest['status']) => {
  if (status === 'approved')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <CheckCircle2 size={12} /> Approved
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <XCircle size={12} /> Rejected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
      <Clock size={12} /> Pending
    </span>
  );
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

const ChangeRequestTaskList: React.FC = () => {
  const [all, setAll] = useState<ChangeRequest[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [loading, setLoading] = useState(true);

  // Action modal state
  const [actionTarget, setActionTarget] = useState<ChangeRequest | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [comment, setComment] = useState('');
  const [actioning, setActioning] = useState(false);

  const currentUserId = localStorage.getItem('userId') || '';

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAllChangeRequests();
      setAll(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const tabItems: ChangeRequest[] = all.filter((r) => r.status === activeTab);

  const openAction = (req: ChangeRequest, type: 'approve' | 'reject') => {
    setActionTarget(req);
    setActionType(type);
    setComment('');
  };

  const closeAction = () => {
    setActionTarget(null);
    setActionType(null);
    setComment('');
  };

  const handleAction = async () => {
    if (!actionTarget || !actionType) return;
    setActioning(true);
    try {
      if (actionType === 'approve') {
        await approveChangeRequest(actionTarget.id, comment || undefined);
      } else {
        await rejectChangeRequest(actionTarget.id, comment || undefined);
      }
      await load();
      closeAction();
    } finally {
      setActioning(false);
    }
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="px-8 pt-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <FileEdit className="w-6 h-6 text-slate-700" />
        <h1 className="text-[22px] font-extrabold text-slate-900">Change Request Tasks</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {tabs.map((tab) => {
          const count = all.filter((r) => r.status === tab.key).length;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                activeTab === tab.key
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
            Loading...
          </div>
        ) : tabItems.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">No {activeTab} change requests</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Document</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Change Reason</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Requested By</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Approver</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Status</th>
                {activeTab === 'pending' && (
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tabItems.map((req) => {
                const isAssignedApprover = req.approverId === currentUserId;
                return (
                  <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{req.documentTitle}</div>
                      <div className="text-xs text-slate-400">{req.documentNumber}</div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-slate-700 line-clamp-2">{req.changeReason}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{req.requestedByName}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {formatDate(req.requestedAt)}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{req.approverName}</td>
                    <td className="px-4 py-3">
                      {statusBadge(req.status)}
                      {req.approverComment && (
                        <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <MessageSquare size={11} /> {req.approverComment}
                        </p>
                      )}
                    </td>
                    {activeTab === 'pending' && (
                      <td className="px-4 py-3">
                        {isAssignedApprover ? (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => openAction(req, 'approve')}
                              className="px-3 py-1 text-xs font-semibold text-green-700 border border-green-200 hover:bg-green-50 rounded-lg transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => openAction(req, 'reject')}
                              className="px-3 py-1 text-xs font-semibold text-red-700 border border-red-200 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Waiting for approver</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Approve / Reject Modal */}
      {actionTarget && actionType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {actionType === 'approve' ? 'Approve Change Request' : 'Reject Change Request'}
              </h3>
              <button type="button" onClick={closeAction} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div className="font-medium text-slate-800">{actionTarget.documentTitle}</div>
                <div className="text-slate-500 mt-1">{actionTarget.changeReason}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Comment <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  placeholder="Add a comment..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={closeAction}
                disabled={actioning}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-[10px] hover:bg-gray-50 transition-colors font-semibold disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAction}
                disabled={actioning}
                className={`flex-1 px-4 py-2 rounded-[10px] font-semibold text-white transition-colors disabled:opacity-50 ${
                  actionType === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actioning ? 'Saving...' : actionType === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeRequestTaskList;
